import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import GenerateTokenModal from "@/components/GenerateTokenModal";
import BackButton from "@/components/BackButton";
import { API } from "@/lib/api";
import { Plus, Trash2, Download, PlayCircle, Copy, Check, Link2, FileText } from "lucide-react";

const STATUS_STYLE = {
  pending: { color: "#38BDF8", bg: "rgba(56,189,248,0.12)", label: "PENDING" },
  completed: { color: "#10B981", bg: "rgba(16,185,129,0.12)", label: "COMPLETED" },
  expired: { color: "#64748B", bg: "rgba(100,116,139,0.15)", label: "EXPIRED" },
};

export default function Tokens() {
  const [tokens, setTokens] = useState([]);
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/tokens");
    setTokens(data);
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [load]);

  const revoke = async (id) => {
    await api.delete(`/tokens/${id}`);
    load();
  };

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(""), 1500);
  };

  return (
    <div>
      <BackButton />
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="eyebrow mb-2">ONE-TIME ACCESS</p>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">Scan Tokens</h1>
        </div>
        <button
          onClick={() => setOpen(true)}
          data-testid="generate-token-modal-button"
          className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-[#04121a] font-semibold rounded-lg px-4 py-2.5 transition-colors"
        >
          <Plus className="w-4 h-4" /> Generate Scan Token
        </button>
      </div>

      <div className="card-soc overflow-hidden">
        {tokens.length === 0 ? (
          <div className="px-6 py-12 text-center text-slate-500 text-sm">No tokens yet.</div>
        ) : (
          <table className="w-full text-sm" data-testid="tokens-table">
            <thead>
              <tr className="text-left eyebrow border-b border-white/8">
                <th className="px-6 py-3 font-normal">Candidate</th>
                <th className="px-6 py-3 font-normal">Interview</th>
                <th className="px-6 py-3 font-normal">Expires</th>
                <th className="px-6 py-3 font-normal">Status</th>
                <th className="px-6 py-3 font-normal">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tokens.map((t) => {
                const st = STATUS_STYLE[t.status] || STATUS_STYLE.pending;
                const usable = t.status === "pending";
                return (
                  <tr key={t.id} data-testid={`token-row-${t.id}`} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="px-6 py-3.5">
                      <div className="text-slate-100 font-medium">{t.candidate_name}</div>
                      <div className="text-xs text-slate-500">{t.candidate_email || "—"}</div>
                    </td>
                    <td className="px-6 py-3.5 text-slate-400 font-mono text-xs">{t.interview_id || "—"}</td>
                    <td className="px-6 py-3.5 text-slate-400 text-xs">
                      {new Date(t.expires_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-3.5">
                      <span
                        className="font-mono text-[10px] font-semibold px-2 py-1 rounded tracking-wider"
                        style={{ color: st.color, background: st.bg }}
                      >
                        {st.label}
                      </span>
                    </td>
                    <td className="px-6 py-3.5">
                      <div className="flex items-center gap-1.5">
                        <button
                          title="Copy token"
                          onClick={() => copy(t.token, t.id)}
                          className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300"
                        >
                          {copied === t.id ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                        </button>
                        <button
                          title="Copy candidate invite link"
                          onClick={() => copy(`${window.location.origin}/scan/${t.token}`, `inv-${t.id}`)}
                          data-testid={`copy-invite-${t.id}`}
                          className="p-2 rounded-lg bg-white/5 hover:bg-emerald-500/15 text-slate-300 hover:text-emerald-300"
                        >
                          {copied === `inv-${t.id}` ? <Check className="w-4 h-4 text-emerald-400" /> : <Link2 className="w-4 h-4" />}
                        </button>
                        <a
                          title="Download scanner"
                          // href={`${API}/agent/download?token=${t.token}`}
                          className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300"
                        >
                          <Download className="w-4 h-4" />
                        </a>
                        <a
                          title="Candidate instructions (PDF)"
                          href={`${API}/tokens/${t.id}/instructions.pdf`}
                          data-testid={`instructions-pdf-${t.id}`}
                          className="p-2 rounded-lg bg-white/5 hover:bg-cyan-500/15 text-slate-300 hover:text-cyan-300"
                        >
                          <FileText className="w-4 h-4" />
                        </a>
                        {usable && (
                          <a
                            title="Open candidate scan page"
                            href={`/scan/${t.token}`}
                            data-testid={`run-demo-${t.id}`}
                            className="p-2 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300"
                          >
                            <PlayCircle className="w-4 h-4" />
                          </a>
                        )}
                        <button
                          title="Revoke"
                          onClick={() => revoke(t.id)}
                          data-testid={`revoke-token-${t.id}`}
                          className="p-2 rounded-lg bg-white/5 hover:bg-red-500/15 text-slate-400 hover:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <GenerateTokenModal open={open} onClose={() => setOpen(false)} onCreated={load} />
    </div>
  );
}
